import { useState } from 'react';
import WeatherHourList from './WeatherHourList';
import { hoursForDay, type Hour } from '../lib/weather';
import type { DayGroup } from '../lib/schedule';
import { dayKey, fmtDayShort } from '../lib/time';

/**
 * The week's weather, in the Schedule's right rail (D67).
 *
 * **Why it is here and not under each day heading.** The first build put a
 * horizontal strip beneath every day's heading, which worked but put weather
 * *inside* the programme — four interruptions between a guest and the thing
 * they opened the page for. The rail is where this page already keeps the
 * things that are true all week rather than at one moment: the wardrobe
 * planner sits directly below. Weather is the same kind of fact, and it reads
 * as reference rather than as part of the schedule.
 *
 * **Phone keeps the strips**, because there is no rail at that width — `.cols`
 * collapses — and a tall card pushed between the heading and the first event
 * would cost the first screen far more than a 44px strip does.
 *
 * **The chips are radios, not buttons.** `.seg` is the control this site
 * already uses for exactly this job (the gallery's tabs), so the keyboard behaviour, the focus ring and the checked styling
 * come for free and match. A row of `<button>`s would have to reimplement all
 * three and would still look slightly different.
 *
 * Days with no forecast are not offered at all rather than shown empty: until
 * about 21 October none of the four are inside Open-Meteo's sixteen-day
 * horizon, so the whole card removes itself and the rail is the wardrobe
 * planner alone, exactly as it is today.
 */
export default function WeatherRail({ hours, days, at }: { hours: Hour[]; days: DayGroup[]; at: number }) {
  /* Before the early return — a hook cannot be conditional, and this component
     genuinely renders nothing most of the time. */
  const [picked, setPicked] = useState<string | null>(null);

  const withWeather = days
    .map((day) => ({ day, cells: hoursForDay(hours, day.events, day.key) }))
    .filter((entry) => entry.cells.length > 0);

  if (withWeather.length === 0) return null;

  /**
   * Today if today is one of them, otherwise the first day we have.
   *
   * `picked` is allowed to be a day that has since dropped out of the list —
   * the forecast moves — so it is looked up rather than trusted, and the
   * fallback chain ends somewhere real.
   */
  const todayKey = dayKey(at);
  const selected = withWeather.find((e) => e.day.key === picked)
    ?? withWeather.find((e) => e.day.key === todayKey)
    ?? withWeather[0]!;

  return (
    <section className="wx-rail" aria-label="Weather">
      <div className="wx-rail-head">
        <h4>Weather</h4>
        <span className="wx-rail-where">Khao Lak</span>
      </div>

      {/* Only worth a control when there is a choice. One day inside the
          horizon — which is how the week begins, around 21 October — shows its
          own heading and no chips to press. */}
      {withWeather.length > 1 && (
        <div className="seg wx-rail-chips">
          {withWeather.map(({ day }) => (
            <label className="seg-opt" key={day.key}>
              <input
                type="radio"
                name="wx-day"
                checked={day.key === selected.day.key}
                onChange={() => setPicked(day.key)}
              />
              <span>{fmtDayShort(day.events[0]!.start)}</span>
            </label>
          ))}
        </div>
      )}

      <div className="wx-rail-day">
        {selected.day.longLabel}
        {selected.day.isToday && <span className="wx-rail-today">Today</span>}
      </div>

      <WeatherHourList hours={selected.cells} label={`Hourly forecast, ${selected.day.longLabel}`} />
    </section>
  );
}
